// src/auth/authManager.ts — Dual-Tier Client Authentication Manager

import type {
  AuthState,
  LoginRequest,
  LoginResponse,
  SetupRequest,
  UserProfile,
} from './authTypes';

const AUTH_TOKEN_KEY = 'clawey_auth_token_v1';
const AUTH_USER_KEY = 'clawey_auth_user_v1';
const STUDENT_DEVICE_TOKEN_KEY = 'clawey_student_device_token_v1';
const FAMILY_PASSCODE_KEY = 'clawey_family_passcode_unlocked_v1';
const CUSTOM_PASSCODE_KEY = 'clawey_custom_family_passcode_v1';
export const DEFAULT_FAMILY_PASSCODE = 'clawey11';

export class AuthManager {
  private static currentState: AuthState = {
    isAuthenticated: false,
    user: null,
    token: null,
  };

  private static listeners: Array<(state: AuthState) => void> = [];
  private static isInitialized = false;

  static resetForTesting(): void {
    this.clearLocalCredentials();
    this.isInitialized = false;
  }

  /**
   * Initializes auth state from local storage.
   */
  static init(): AuthState {
    if (this.isInitialized) return this.currentState;

    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const userRaw = localStorage.getItem(AUTH_USER_KEY);
      if (token && userRaw) {
        const user: UserProfile = JSON.parse(userRaw);
        const now = Date.now();
        if (user.expiresAt > now) {
          this.currentState = {
            isAuthenticated: true,
            user,
            token,
          };
        } else {
          // Expired
          this.clearLocalCredentials();
        }
      }
    } catch {
      this.clearLocalCredentials();
    }

    this.isInitialized = true;
    return this.currentState;
  }

  static getState(): AuthState {
    if (!this.isInitialized) {
      this.init();
    }
    return { ...this.currentState };
  }

  static getUser(): UserProfile | null {
    return this.getState().user;
  }

  static getToken(): string | null {
    return this.getState().token;
  }

  static isParentAuthenticated(): boolean {
    const state = this.getState();
    return state.isAuthenticated && state.user?.role === 'parent';
  }

  static isStudentAuthenticated(): boolean {
    const state = this.getState();
    if (state.isAuthenticated && (state.user?.role === 'student' || state.user?.role === 'parent')) {
      return true;
    }
    // Check if student device token exists for frictionless 1-tap tablet access
    return Boolean(localStorage.getItem(STUDENT_DEVICE_TOKEN_KEY));
  }

  /**
   * Returns true if either the family passcode has been entered or user is logged in.
   */
  static isFamilyUnlocked(): boolean {
    try {
      const unlocked = localStorage.getItem(FAMILY_PASSCODE_KEY);
      if (unlocked === 'true' || this.isParentAuthenticated() || this.isStudentAuthenticated()) {
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  static getPresetPasscodeHint(): string {
    return DEFAULT_FAMILY_PASSCODE;
  }

  /**
   * Unlocks the entire site using the family passcode or parent master key.
   */
  static async unlockWithFamilyPasscode(
    passcode: string,
    role: 'student' | 'parent' = 'student',
    remember: boolean = true
  ): Promise<boolean> {
    const clean = passcode.trim().toLowerCase();
    const customPasscode = (localStorage.getItem(CUSTOM_PASSCODE_KEY) || '').trim().toLowerCase();
    const matchesFamily = clean === DEFAULT_FAMILY_PASSCODE || (customPasscode && clean === customPasscode);

    if (role === 'student' && matchesFamily) {
      if (remember) {
        localStorage.setItem(FAMILY_PASSCODE_KEY, 'true');
        localStorage.setItem(STUDENT_DEVICE_TOKEN_KEY, `student-token-${Date.now()}`);
      }
      const studentProfile: UserProfile = {
        id: 'student-tudor',
        role: 'student',
        name: 'Tudor',
        expiresAt: Date.now() + 90 * 24 * 60 * 60 * 1000,
      };
      this.saveCredentials('student-passcode-token', studentProfile, true);
      return true;
    }

    if (role === 'parent') {
      // 1. Try real parent authentication with password
      const result = await this.login({
        role: 'parent',
        secret: passcode.trim(),
        rememberMe: remember,
      });

      if (result.success) {
        if (remember) {
          localStorage.setItem(FAMILY_PASSCODE_KEY, 'true');
        }
        return true;
      }

      // 2. Also accept family passcode for parent entry
      if (matchesFamily) {
        const parentProfile: UserProfile = {
          id: 'parent-primary',
          role: 'parent',
          name: 'Florin',
          expiresAt: Date.now() + 30 * 24 * 60 * 60 * 1000,
        };
        this.saveCredentials('parent-passcode-token', parentProfile, false);
        if (remember) {
          localStorage.setItem(FAMILY_PASSCODE_KEY, 'true');
        }
        return true;
      }
    }

    // Fallback: If role wasn't specified but passcode matches family passcode, unlock as student
    if (matchesFamily) {
      if (remember) {
        localStorage.setItem(FAMILY_PASSCODE_KEY, 'true');
        localStorage.setItem(STUDENT_DEVICE_TOKEN_KEY, `student-token-${Date.now()}`);
      }
      const studentProfile: UserProfile = {
        id: 'student-tudor',
        role: 'student',
        name: 'Tudor',
        expiresAt: Date.now() + 90 * 24 * 60 * 60 * 1000,
      };
      this.saveCredentials('student-passcode-token', studentProfile, true);
      return true;
    }

    return false;
  }

  /**
   * Performs login against /api/auth/login with offline fallback.
   */
  static async login(req: LoginRequest): Promise<LoginResponse> {
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(req),
      });

      const data: LoginResponse = await res.json();
      if (data.success && data.token && data.user) {
        this.saveCredentials(data.token, data.user, req.role === 'student');
        return data;
      }
      return data;
    } catch {
      // Offline fallback: If offline and checking student 1-tap or known device
      if (req.role === 'student') {
        const offlineStudent: UserProfile = {
          id: 'tudor-device-offline',
          role: 'student',
          name: 'Tudor',
          expiresAt: Date.now() + 90 * 24 * 60 * 60 * 1000,
        };
        const syntheticToken = 'offline-student-token';
        this.saveCredentials(syntheticToken, offlineStudent, true);
        return { success: true, token: syntheticToken, user: offlineStudent };
      }

      return {
        success: false,
        error: 'Unable to reach authentication server. Please verify network connection.',
      };
    }
  }

  /**
   * Setup initial parent password on first run.
   */
  static async setupParent(req: SetupRequest): Promise<LoginResponse> {
    try {
      const res = await fetch('/api/auth/setup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(req),
      });

      const data: LoginResponse = await res.json();
      if (data.success && data.token && data.user) {
        this.saveCredentials(data.token, data.user, false);
      }
      return data;
    } catch (err: any) {
      return { success: false, error: err?.message || 'Setup request failed' };
    }
  }

  /**
   * Logs out active user and revokes remote session if reachable.
   */
  static async logout(): Promise<void> {
    const token = this.currentState.token;
    if (token) {
      try {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
      } catch {
        // Continue local cleanup even if remote call fails
      }
    }

    this.clearLocalCredentials();
    this.notifyListeners();
  }

  /**
   * Quick student 1-tap fast unlock on registered tablet.
   */
  static async unlockStudentFast(): Promise<boolean> {
    const result = await this.login({
      role: 'student',
      secret: '',
      deviceName: 'Samsung Galaxy Tab A9+',
    });
    return result.success;
  }

  /**
   * Subscribe to auth changes.
   */
  static onAuthStateChange(callback: (state: AuthState) => void): () => void {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter((cb) => cb !== callback);
    };
  }

  private static saveCredentials(token: string, user: UserProfile, isStudent: boolean): void {
    try {
      localStorage.setItem(AUTH_TOKEN_KEY, token);
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
      if (isStudent) {
        localStorage.setItem(STUDENT_DEVICE_TOKEN_KEY, token);
      }
    } catch {
      // LocalStorage error guard
    }

    this.currentState = {
      isAuthenticated: true,
      user,
      token,
    };
    this.notifyListeners();
  }

  private static clearLocalCredentials(): void {
    try {
      localStorage.removeItem(AUTH_TOKEN_KEY);
      localStorage.removeItem(AUTH_USER_KEY);
      localStorage.removeItem(STUDENT_DEVICE_TOKEN_KEY);
      localStorage.removeItem(FAMILY_PASSCODE_KEY);
    } catch {
      // ignore
    }

    this.currentState = {
      isAuthenticated: false,
      user: null,
      token: null,
    };
  }

  private static notifyListeners(): void {
    const state = { ...this.currentState };
    this.listeners.forEach((cb) => {
      try {
        cb(state);
      } catch (e) {
        console.error('Auth listener error:', e);
      }
    });
  }
}
